/**
 * One quarter, one row per borrower: where it stands in plain words and the
 * one thing to do next (DOP-625, after the deal screening redesign).
 *
 * This is the page a credit analyst opens on the Monday after the package
 * deadline. Three tiles answer the first questions — what needs me, where are
 * the covenants, are the packages in — and the table below says per borrower
 * what `borrowerStanding` says, with one button: Run when the package is in and
 * not run, Open for any quarter with a run that is not locked yet, Locked once
 * it is, nothing while the package is out. Locking is always an explicit step
 * on the run page, even when every covenant passes.
 *
 * Re-running a borrower after a corrected file is still one click away
 * (DOP-458): rows with a run carry "Run again" in their ⋯ menu, which writes a
 * new version. Adding borrowers lives on the Borrowers tab; an empty board
 * still offers it, because that is the first thing a new template needs.
 */

import { AlertTriangle, CircleAlert, FolderOpen, Loader2, Lock, MoreHorizontal, Plus } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
	MonitoringBoard,
	MonitoringBoardRow,
	MonitoringBorrowerCandidateGroup,
	MonitoringCreateBorrowerPayload,
} from "../../types/monitoring";
import AddBorrowerForm, { type BorrowerCreateResult } from "./AddBorrowerForm";
import {
	type BorrowerStanding,
	type BorrowerStandingKind,
	borrowerStanding,
	needsYou,
} from "./borrowerStanding";
import { formatEpochMs, formatIsoDate } from "./monitoringFormat";

/** Where DOP-452's run page lives. Kept here so one file owns the shape. */
export const runPath = (runPublicId: string): string =>
	`/workflows/monitoring/runs/${runPublicId}`;

/** Pill colours per standing, the Deals home's. Every colour also carries its words. */
export const STANDING_PILL: Record<BorrowerStandingKind, string> = {
	waiting: "border-gray-200 bg-white text-[#5B6472]",
	late: "border-[#fecdca] bg-white text-[#b42318]",
	ready_to_run: "border-gray-200 bg-white text-[#5B6472]",
	running: "border-[#CCE7E6] bg-[#E6F3F3] text-[#004C4B]",
	failed: "border-[#fecdca] bg-[#fef3f2] text-[#b42318]",
	checks: "border-[#fedf89] bg-[#fffaeb] text-[#b54708]",
	breach: "border-[#fecdca] bg-[#fef3f2] text-[#b42318]",
	near: "border-[#fedf89] bg-[#fffaeb] text-[#b54708]",
	pass: "border-[#abefc6] bg-[#ecfdf3] text-[#067647]",
	locked: "border-transparent bg-[#f2f2f2] text-[#3f4247]",
};

const ROW_TINT: Partial<Record<BorrowerStandingKind, string>> = {
	breach: "bg-[#fffbfa]",
	checks: "bg-[#fffdf7]",
};

const BUTTON =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] disabled:opacity-50";
const SECONDARY = `${BUTTON} border-gray-200 bg-white text-[#1F2937] hover:bg-gray-50`;
/** Quiet: a locked quarter is done; the link only opens it to read. */
const LOCKED = `${BUTTON} border-transparent bg-transparent text-[#6B7280] hover:bg-gray-50`;
const RUN = `${BUTTON} border-[#99CFCD] bg-white text-[#004C4B] hover:bg-[#f6fbfb]`;

/** Two letters for the row's tile: "Meadowbrook Surgical" → "MS". */
const initials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word.charAt(0).toUpperCase())
		.join("");

/** The row's sub line: documents and when it last moved. */
function subLine(row: MonitoringBoardRow): string {
	const docs = row.package.documents.length;
	const documents = docs === 1 ? "1 document" : `${docs} documents`;
	const run = row.run;
	if (!run) return row.package.status === "not_received" ? "No package yet" : `${documents} in the Deal Room`;
	if (run.locked_at) {
		return `${documents} · locked ${formatEpochMs(run.locked_at)}${run.locked_by_name ? ` by ${run.locked_by_name}` : ""}`;
	}
	return `${documents} · v${run.version}${run.generated_at ? ` run ${formatEpochMs(run.generated_at)}` : ""}`;
}

interface TileProps {
	tone: "amber" | "red" | "neutral";
	icon: React.ReactNode;
	title: string;
	value: string;
	sub: string;
	onClick?: () => void;
}

const TILE_TONE: Record<TileProps["tone"], { box: string; title: string }> = {
	amber: { box: "border-[#fedf89] bg-[#fffcf2]", title: "text-[#b54708]" },
	red: { box: "border-[#fecdca] bg-[#fffbfa]", title: "text-[#b42318]" },
	neutral: { box: "border-gray-200 bg-white", title: "text-[#004C4B]" },
};

const Tile: React.FC<TileProps> = ({ tone, icon, title, value, sub, onClick }) => {
	const body = (
		<>
			<span className={`flex items-center gap-2 text-[13px] font-semibold ${TILE_TONE[tone].title}`}>
				{icon}
				{title}
			</span>
			<span className="text-[26px] font-bold leading-tight text-[#111111]">{value}</span>
			<span className="text-[13px] text-[#5B6472]">{sub}</span>
		</>
	);
	const className = `flex flex-col gap-1 rounded-xl border px-[18px] py-3.5 text-left ${TILE_TONE[tone].box}`;
	return onClick ? (
		<button type="button" onClick={onClick} className={`${className} hover:shadow-sm`}>
			{body}
		</button>
	) : (
		<div className={className}>{body}</div>
	);
};

interface RowMenuProps {
	borrowerName: string;
	onRunAgain: () => void;
	disabled: boolean;
}

/** The row's ⋯: "Run again", out of the way of the next step. */
const RowMenu: React.FC<RowMenuProps> = ({ borrowerName, onRunAgain, disabled }) => {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!open) return;
		const close = (event: MouseEvent) => {
			if (!ref.current?.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", close);
		return () => document.removeEventListener("mousedown", close);
	}, [open]);
	return (
		<div className="relative" ref={ref}>
			<button
				type="button"
				onClick={() => setOpen((was) => !was)}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={`More for ${borrowerName}`}
				className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-gray-100 hover:text-[#111111]"
			>
				<MoreHorizontal className="h-4 w-4" aria-hidden />
			</button>
			{open && (
				<div role="menu" className="absolute right-0 z-10 mt-1 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
					<button
						type="button"
						role="menuitem"
						disabled={disabled}
						onClick={() => {
							setOpen(false);
							onRunAgain();
						}}
						className="block w-full px-3 py-2 text-left text-sm text-[#1F2937] hover:bg-gray-50 disabled:opacity-50"
						title="Writes a new version from the package as it is now"
					>
						Run again
					</button>
				</div>
			)}
		</div>
	);
};

interface NextStepProps {
	row: MonitoringBoardRow;
	standing: BorrowerStanding;
	running: boolean;
	/** A period-wide run is being queued; a row's own Run would double it. */
	frozen: boolean;
	onRun: () => void;
}

const NextStep: React.FC<NextStepProps> = ({ row, standing, running, frozen, onRun }) => {
	const name = row.borrower.name;
	const run = row.run;
	if (running) {
		return (
			<button type="button" disabled className={SECONDARY} aria-label={`Run ${name}`}>
				<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
				Starting…
			</button>
		);
	}
	switch (standing.next) {
		case "locked":
			return run ? (
				<Link to={runPath(run.public_id)} className={LOCKED} aria-label={`Locked ${name}`}>
					<Lock className="h-3.5 w-3.5" aria-hidden />
					Locked
				</Link>
			) : null;
		case "run":
			return (
				<button
					type="button"
					onClick={onRun}
					disabled={frozen}
					className={RUN}
					aria-label={`${run ? "Try again" : "Run"} ${name}`}
				>
					{run ? "Try again" : "Run"}
				</button>
			);
		case "open":
			return run ? (
				<Link to={runPath(run.public_id)} className={SECONDARY} aria-label={`Open ${name}`}>
					Open
				</Link>
			) : null;
		default:
			return <span className="text-xs text-[#9a9ea3]">{standing.kind === "running" ? "Running" : "Waiting"}</span>;
	}
};

export interface PeriodBoardProps {
	board: MonitoringBoard;
	/** True while a refetch is in flight; the board dims instead of blanking. */
	stale: boolean;
	onRunBorrower: (borrowerId: string) => void;
	runningBorrowerIds: ReadonlySet<string>;
	/** "Run received packages" is in flight; per-row runs wait for it. */
	runAllStarting: boolean;
	/** The quarter after this one, for the footer line. */
	nextPeriod: { label: string; package_due: string } | null;
	borrowerCandidates: MonitoringBorrowerCandidateGroup[];
	onCreateBorrower: (payload: MonitoringCreateBorrowerPayload) => Promise<BorrowerCreateResult>;
	onBorrowerAdded: () => void;
	/** Opens the Needs you tab, from its tile. */
	onShowNeedsYou?: () => void;
	/** For tests: what "today" is when counting days late. */
	today?: Date;
}

const PeriodBoard: React.FC<PeriodBoardProps> = ({
	board,
	stale,
	onRunBorrower,
	runningBorrowerIds,
	runAllStarting,
	nextPeriod,
	borrowerCandidates,
	onCreateBorrower,
	onBorrowerAdded,
	onShowNeedsYou,
	today,
}) => {
	const [adding, setAdding] = useState(false);
	const rows = board.rows.map((row) => ({ row, standing: borrowerStanding(row, board.period, today) }));

	if (board.rows.length === 0) {
		return (
			<div className="space-y-4">
				<div className="rounded-xl border border-gray-200 bg-white px-6 py-10 text-center">
					<p className="text-sm font-semibold text-[#111111]">No borrowers yet</p>
					<p className="mx-auto mt-1 max-w-md text-sm text-[#5B6472]">
						Add the borrowers this fund lends to. Each quarter, their packages are read into the
						workbook and tested against their covenants.
					</p>
					<button
						type="button"
						onClick={() => setAdding(true)}
						aria-expanded={adding}
						className={`${SECONDARY} mt-4`}
					>
						<Plus className="h-4 w-4" aria-hidden />
						Add borrower
					</button>
				</div>
				{adding && (
					<AddBorrowerForm
						candidates={borrowerCandidates}
						onCreate={onCreateBorrower}
						onAdded={onBorrowerAdded}
						onDone={() => setAdding(false)}
						onCancel={() => setAdding(false)}
					/>
				)}
			</div>
		);
	}

	const waitingOnYou = rows.filter(({ standing }) => needsYou(standing));
	const openChecks = rows.reduce((sum, { row }) => sum + (row.run?.gaps_open ?? 0), 0);
	const notRun = rows.filter(({ standing }) => standing.kind === "ready_to_run").length;
	const outcome = (kind: string) => rows.filter(({ row }) => row.run && row.covenants.worst === kind).length;
	const breaches = outcome("breach");
	const near = outcome("near");
	const pass = outcome("pass");
	const packagesIn = board.counts.received + board.counts.partial;
	const late = rows.filter(({ standing }) => standing.kind === "late");

	return (
		<div className={`space-y-4 ${stale ? "opacity-60" : ""}`}>
			<p className="text-sm text-[#5B6472]">
				Each borrower's {board.period.label} package, read into your monitoring workbook with every
				figure sourced. Packages were due {formatIsoDate(board.period.package_due)}.
			</p>
			<div className="grid gap-3.5 md:grid-cols-3" data-testid="board-tiles">
				<Tile
					tone={waitingOnYou.length > 0 ? "amber" : "neutral"}
					icon={<CircleAlert className="h-4 w-4" aria-hidden />}
					title="Needs you"
					value={waitingOnYou.length === 1 ? "1 borrower" : `${waitingOnYou.length} borrowers`}
					sub={
						waitingOnYou.length === 0
							? "Nothing open this quarter"
							: [
									openChecks > 0 ? `${openChecks} open ${openChecks === 1 ? "check" : "checks"}` : null,
									notRun > 0 ? `${notRun} ${notRun === 1 ? "package" : "packages"} not run` : null,
									late.length > 0 ? `${late.length} late` : null,
								]
									.filter(Boolean)
									.join(" · ") || "Covenant breaches to review"
					}
					onClick={waitingOnYou.length > 0 ? onShowNeedsYou : undefined}
				/>
				<Tile
					tone={breaches > 0 ? "red" : "neutral"}
					icon={<AlertTriangle className="h-4 w-4" aria-hidden />}
					title="Covenants"
					value={breaches > 0 ? `${breaches} ${breaches === 1 ? "breach" : "breaches"}` : `${pass} all pass`}
					sub={
						[breaches > 0 ? `${pass} all pass` : null, near > 0 ? `${near} near threshold` : null]
							.filter(Boolean)
							.join(" · ") || "Across the borrowers run so far"
					}
				/>
				<Tile
					tone="neutral"
					icon={<FolderOpen className="h-4 w-4" aria-hidden />}
					title="Packages"
					value={`${packagesIn} of ${board.counts.borrowers} in`}
					sub={
						late.length === 0
							? "None late"
							: late.length === 1
								? `${late[0].row.borrower.name} is late`
								: `${late.length} late`
					}
				/>
			</div>

			<div className="rounded-xl border border-gray-200 bg-white" data-testid="period-board">
				<div className="flex items-center gap-3.5 rounded-t-xl border-b border-gray-200 bg-[#fafafa] px-5 py-2.5 text-xs font-semibold text-[#5B6472]">
					<span className="flex-1">Borrower</span>
					<span className="hidden w-80 md:block">Where it stands</span>
					<span className="w-36 text-right">Next step</span>
				</div>
				<ul>
					{rows.map(({ row, standing }) => {
						const running = runningBorrowerIds.has(row.borrower.public_id);
						const quiet = standing.kind === "waiting" || standing.kind === "late" || standing.kind === "locked";
						return (
							<li
								key={row.borrower.public_id}
								className={`flex items-center gap-3.5 border-b border-[#f1f2f4] px-5 py-3 last:rounded-b-xl last:border-0 ${ROW_TINT[standing.kind] ?? ""}`}
								data-testid={`board-row-${row.borrower.public_id}`}
							>
								<span
									className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
										standing.kind === "breach"
											? "bg-[#fef3f2] text-[#b42318]"
											: quiet
												? "bg-[#f2f2f2] text-[#6B7280]"
												: "bg-[#E6F3F3] text-[#004C4B]"
									}`}
									aria-hidden
								>
									{initials(row.borrower.name)}
								</span>
								<div className="min-w-0 flex-1">
									<p className="truncate text-[14px] font-semibold text-[#111111]">{row.borrower.name}</p>
									<p className="mt-0.5 truncate text-xs text-[#6B7280]">{subLine(row)}</p>
								</div>
								<div className="hidden w-80 md:block">
									<span
										className={`inline-flex h-[26px] max-w-full items-center gap-1.5 truncate whitespace-nowrap rounded-full border px-2.5 text-xs font-semibold ${STANDING_PILL[standing.kind]}`}
										title={standing.kind === "failed" ? (standing.detail ?? undefined) : undefined}
									>
										{standing.kind === "locked" && <Lock className="h-3 w-3" aria-hidden />}
										{standing.kind === "running" && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
										{standing.label}
									</span>
									{standing.detail && standing.kind !== "failed" && (
										<p className="mt-1 truncate text-xs text-[#6B7280]">{standing.detail}</p>
									)}
								</div>
								<div className="flex w-36 items-center justify-end gap-1.5">
									<NextStep
										row={row}
										standing={standing}
										running={running}
										frozen={runAllStarting}
										onRun={() => onRunBorrower(row.borrower.public_id)}
									/>
									{row.run && row.run.status !== "locked" && standing.next !== "run" && (
										<RowMenu
											borrowerName={row.borrower.name}
											disabled={runAllStarting || running || row.run.generating}
											onRunAgain={() => onRunBorrower(row.borrower.public_id)}
										/>
									)}
								</div>
							</li>
						);
					})}
				</ul>
			</div>

			{nextPeriod && (
				<p className="text-xs text-[#6B7280]">
					Next up: {nextPeriod.label} packages are due {formatIsoDate(nextPeriod.package_due)}.
				</p>
			)}
		</div>
	);
};

export default PeriodBoard;
